<template>
  <div class="widgetbuilder">
    <h5 class="wb-heading">Widget builder</h5>
    <!-- Contract §7. Every control on this screen is built from GET v1/widgetComposerOptions:
         the reports that can be aggregated over, their real columns, the aggregates, the date
         periods and the comparison periods. Nothing here is a second copy of that vocabulary,
         so the screen cannot offer something the server would then refuse. -->
    <p class="wb-note">
      Build a widget for the catalogue: pick a report, what to measure on it, and how it is
      labelled. Everything on offer comes from the server, so anything you can pick here is
      something it will accept.
    </p>

    <ProgressSpinner
      v-if="loading"
      style="width:25px; height:25px; width: 100%;"
      strokeWidth="5"
      fill="#EEEEEE"
      animationDuration=".5s"
    />

    <div v-else class="wb-form">
      <div class="wb-field">
        <label class="wb-label" for="wb-widget">Widget</label>
        <!-- Which widget this form is about: one already in the catalogue, or a new one. The
             catalogue comes from GET v1/dashboardWidgets and the settings behind an entry from
             GET v1/dashboardWidgets/{id}/definition, so what is loaded here is what the server
             holds, and saving it hands the same thing back. -->
        <Dropdown
          id="wb-widget"
          class="wb-widget"
          :value="selectedWidgetId"
          :options="widgetOptions"
          optionLabel="label"
          optionValue="value"
          optionDisabled="disabled"
          placeholder="New widget"
          @input="chooseWidget($event)"
        />
        <!-- The legacy widgets are shown, greyed, never dropped from the list: a widget that
             quietly went missing would read as a fault, and the reason it cannot be edited is
             worth a line. -->
        <small v-if="hasLegacyWidgets" class="wb-legacy-note">{{ legacyNote }}</small>
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-report">Report</label>
        <Dropdown
          id="wb-report"
          class="wb-report"
          :value="form.reportId"
          :options="reportOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose a report"
          @input="setReport($event)"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-aggregate">Measure</label>
        <Dropdown
          id="wb-aggregate"
          class="wb-aggregate"
          :value="form.aggregate"
          :options="aggregateOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose a measure"
          @input="setAggregate($event)"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-column">Column</label>
        <!-- sum and avg are offered only the columns the server marked aggregatable, so a sum
             of a date is never on offer in the first place (contract §6.1). count takes any
             column. -->
        <Dropdown
          id="wb-column"
          class="wb-column"
          :value="form.column"
          :options="columnOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose a column"
          @input="form.column = $event"
        />
      </div>

      <div v-if="isGroupedReport" class="wb-field">
        <label class="wb-label" for="wb-totalfield">Tile equals</label>
        <!-- W5c contract §3. A grouped report answers with one line per group, not with the rows
             the tile counts, so the proof adds up one of its summary columns across every line.
             Which one is the admin's to say; the choices and their labels come from the report
             (totalFields). A report that lists rows has no such column, so this is not shown. -->
        <Dropdown
          id="wb-totalfield"
          class="wb-totalfield"
          :value="form.drillTotalField"
          :options="totalFieldOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose the report column the tile equals"
          @input="form.drillTotalField = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-predicate">Only rows where (optional)</label>
        <InputText
          id="wb-predicate"
          class="wb-predicate"
          :value="form.predicate"
          placeholder="oppstatusid = 4"
          @input="form.predicate = $event"
        />
        <small v-if="drillNote" class="wb-drill-note">{{ drillNote }}</small>
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-title">Title</label>
        <InputText
          id="wb-title"
          class="wb-title"
          :value="form.title"
          placeholder="Won this month"
          @input="form.title = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-icon">Icon</label>
        <Dropdown
          id="wb-icon"
          class="wb-icon"
          :value="form.icon"
          :options="iconOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose an icon"
          @input="form.icon = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-valuetype">Shown as</label>
        <Dropdown
          id="wb-valuetype"
          class="wb-valuetype"
          :value="form.valueType"
          :options="valueTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose how the figure is shown"
          @input="form.valueType = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-datefield">Date field</label>
        <!-- Only the report's own date columns: a date window is a date column plus a period,
             and both together are what makes a comparison possible at all. -->
        <Dropdown
          id="wb-datefield"
          class="wb-datefield"
          :value="form.dateField"
          :options="dateFieldOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="No date field"
          @input="form.dateField = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-dateperiod">Period</label>
        <Dropdown
          id="wb-dateperiod"
          class="wb-dateperiod"
          :value="form.datePeriod"
          :options="datePeriodOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="No period"
          @input="form.datePeriod = $event"
        />
      </div>

      <div class="wb-field">
        <label class="wb-label" for="wb-compare">Compare with</label>
        <!-- Disabled, never hidden (decision 7, contract §7): a widget with no date window has
             no earlier window to compare with, and a setting that silently disappears reads as
             a bug. It stays on screen, greyed, with the reason next to it. -->
        <Dropdown
          id="wb-compare"
          class="wb-compare"
          :value="form.comparePeriod"
          :options="comparePeriodOptions"
          optionLabel="label"
          optionValue="value"
          :disabled="!compareAvailable"
          placeholder="No comparison"
          @input="form.comparePeriod = $event"
        />
        <small v-if="compareNote" class="wb-compare-note">{{ compareNote }}</small>
      </div>

      <!-- The server's own words, as they arrive (contract §6 and §7). They name the part that
           was wrong; re-wording them here would hide which part. The form keeps everything the
           user typed so they can correct that part and save again. -->
      <div v-if="errorMessage" class="wb-error">{{ errorMessage }}</div>
      <div v-if="savedMessage" class="wb-saved">{{ savedMessage }}</div>

      <Button
        class="wb-save"
        label="Save widget"
        :disabled="!canSave || saving || loadingDefinition"
        @click="save"
      />
    </div>
  </div>
</template>

<script>
import UserService from "@/service/UserService";

// The admin composes a catalogue widget (item 4 W5, contract §6, §6.1 and §7).
//
// An admin never writes SQL: a widget is a report, an aggregate over one of that report's own
// columns, an optional predicate from the same constrained grammar, and the labelling. Every
// part is validated server-side against a whitelist, and this screen asks the server what that
// whitelist contains rather than keeping its own copy.
//
// There is deliberately no delete: a catalogue widget may already be on other people's
// dashboards, and W3 made removal a per-user deactivation (contract §6).
//
// No alert, no confirm, no blocking dialog anywhere: a modal freezes the browser automation for
// every session on this machine, and none of what happens here needs one - a rejection is a line
// of text next to the form the user is already looking at.

// sum and avg need a number; count does not care what it counts.
const NEEDS_NUMBER = ['sum', 'avg']

// A stored aggregate, taken apart into the measure and the column the form holds separately.
// The server validated it before storing it, so anything that does not come apart is not
// something this form can safely put back together.
const AGG_EXPR = /^\s*([A-Za-z_]+)\s*\(\s*([^()]*?)\s*\)\s*$/

// A label for a value that arrived as a code. Generic - it turns whatever the server sent into
// something readable, and never decides what is on offer.
function prettify(value) {
  const text = value === null || value === undefined ? '' : String(value)
  if (!text) return ''
  const spaced = text.replace(/_/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

// A dropdown's options, from a plain list of codes.
function asOptions(values) {
  return (Array.isArray(values) ? values : []).map(value => ({
    label: prettify(value),
    value: value
  }))
}

function emptyForm() {
  return {
    // The catalogue name, as it was loaded. Not sent: the body's widgetName is the title (W5c
    // contract §5.1) - sending the stored name made the server keep it and drop an edited title.
    name: '',
    widgetType: 'WIDGET',
    reportId: null,
    aggregate: null,
    column: null,
    predicate: '',
    title: '',
    icon: null,
    valueType: null,
    dateField: null,
    datePeriod: null,
    comparePeriod: null,
    // Grouped reports only (W5c contract §3): the report column the tile equals, summed over
    // every group line. null for a report that lists rows.
    drillTotalField: null
  }
}

// The form that a stored definition fills in. Every field of the compose body has a home here
// and comes back out of widgetBody unchanged, which is what makes loading and saving a widget
// without touching it a no-op on the server. null when the aggregate cannot be read.
function formFromDefinition(definition) {
  const parts = AGG_EXPR.exec(String(definition.aggExpr === null || definition.aggExpr === undefined ? '' : definition.aggExpr))
  if (!parts) return null
  return {
    name: definition.widgetName || '',
    widgetType: definition.widgetType || 'WIDGET',
    reportId: definition.reportId === undefined ? null : definition.reportId,
    aggregate: parts[1].toLowerCase(),
    column: parts[2],
    predicate: definition.basePredicate || '',
    title: definition.widgetTitle || definition.widgetName || '',
    icon: definition.widgetIcon || null,
    valueType: definition.valueType || null,
    dateField: definition.dateField || null,
    datePeriod: definition.datePeriod || null,
    comparePeriod: definition.widgetComparePeriod || null,
    drillTotalField: definition.drillTotalField || null
  }
}

// A catalogue entry that still has no report and no aggregate is one of the legacy nine: it is
// drawn by the old get_widget_data path (W1's fallback), and saving it from this form would turn
// it into a definition-backed widget and change the figure every user on the system sees.
function isEditable(entry) {
  if (!entry) return false
  const hasReport = entry.reportId !== null && entry.reportId !== undefined
  const hasAggregate = !!String(entry.aggExpr === null || entry.aggExpr === undefined ? '' : entry.aggExpr).trim()
  return hasReport && hasAggregate
}

function idOf(entry) {
  if (!entry) return null
  return entry.widgetId === null || entry.widgetId === undefined ? entry.id : entry.widgetId
}

export default {
  created() {
    this.getOptions()
    this.getCatalogue()
  },
  data: () => ({
    userService: new UserService(),
    // Exactly what GET v1/widgetComposerOptions returned, kept whole. Every control below is
    // built from this and from nothing else.
    options: {
      reports: [],
      aggregates: [],
      datePeriods: [],
      comparePeriods: [],
      valueTypes: [],
      icons: []
    },
    form: emptyForm(),
    // The catalogue, exactly as GET v1/dashboardWidgets returned it: what the picker offers and
    // what says which of them are still on the older calculation.
    catalogue: [],
    // The widget the picker is showing; null is "New widget".
    selectedWidgetId: null,
    // Set once a widget has been created or loaded, so a save updates it rather than creating a
    // duplicate of it.
    widgetId: null,
    // The report filters the widget's link carries, as the server stored them once it had proven
    // that the tile and the report agree (W5b). Read-only: the body never sends it.
    drillFilters: null,
    // Which report the widget's link opens, as the server stored it (W5c contract §2): null means
    // the widget's own report. drillFromReportId is the report the widget was stored on, so the
    // note describes what is stored rather than whatever the form has since been changed to.
    drillReportId: null,
    drillReportName: null,
    drillFromReportId: null,
    loading: false,
    loadingDefinition: false,
    saving: false,
    errorMessage: '',
    savedMessage: ''
  }),
  computed: {
    // The picker: a way back to composing a new widget, then the catalogue in the order the
    // server sent it. A widget still on the older calculation is in the list but cannot be
    // chosen - `optionDisabled` is the field PrimeVue reads to refuse the click.
    widgetOptions() {
      const entries = (this.catalogue || []).map(entry => ({
        label: String(entry.widgetTitle || entry.widgetName || '').trim() || ('Widget ' + idOf(entry)),
        value: idOf(entry),
        disabled: !isEditable(entry)
      }))
      return [{ label: 'New widget', value: null, disabled: false }].concat(entries)
    },
    hasLegacyWidgets() {
      return (this.catalogue || []).some(entry => !isEditable(entry))
    },
    legacyNote() {
      return 'The greyed-out widgets are still on the older calculation and cannot be edited ' +
        'here - editing one would change the figure everybody sees.'
    },
    // What the tile's link will open its report with. Only a widget that has been saved has one,
    // because the server works it out at save time by proving the report agrees with the tile.
    drillNote() {
      if (!this.widgetId) return ''
      // W5c contract §4.4: the proof's third candidate won, so the link opens another report -
      // "Rows behind this widget" - named as the server named it.
      const opens = String(this.drillReportName === null || this.drillReportName === undefined ? '' : this.drillReportName).trim()
      if (this.drillReportId !== null && this.drillReportId !== undefined &&
          this.drillReportId !== this.drillFromReportId && opens) {
        const from = (this.options.reports || []).find(report => report.id === this.drillFromReportId)
        const fromName = from && from.name ? from.name : 'the widget\'s report'
        return 'Its link opens \'' + opens + '\', because \'' + fromName +
          '\' cannot be filtered to match it exactly.'
      }
      const filters = String(this.drillFilters === null || this.drillFilters === undefined ? '' : this.drillFilters).trim()
      return filters
        ? 'Its link opens the report with the filters ' + filters + '.'
        : 'Its link opens the report with no filters of its own - the report already shows only these rows.'
    },
    reportOptions() {
      return (this.options.reports || []).map(report => ({
        label: report.name,
        value: report.id
      }))
    },
    aggregateOptions() {
      return asOptions(this.options.aggregates)
    },
    valueTypeOptions() {
      return asOptions(this.options.valueTypes)
    },
    iconOptions() {
      return asOptions(this.options.icons)
    },
    datePeriodOptions() {
      return asOptions(this.options.datePeriods)
    },
    selectedReport() {
      const id = this.form.reportId
      if (id === null || id === undefined) return null
      return (this.options.reports || []).find(report => report.id === id) || null
    },
    reportColumns() {
      const report = this.selectedReport
      return report && Array.isArray(report.columns) ? report.columns : []
    },
    // A report that answers with group lines rather than rows (W5c contract §3), as the server
    // says - reports 9, 10 and 11 today.
    isGroupedReport() {
      return !!(this.selectedReport && String(this.selectedReport.kind).toLowerCase() === 'groups')
    },
    totalFieldOptions() {
      const fields = this.isGroupedReport && Array.isArray(this.selectedReport.totalFields)
        ? this.selectedReport.totalFields
        : []
      return fields.map(field => ({ label: field.label || field.name, value: field.name }))
    },
    // sum and avg see only the columns the server marked aggregatable; count sees them all.
    columnOptions() {
      const aggregate = (this.form.aggregate || '').toLowerCase()
      const columns = NEEDS_NUMBER.indexOf(aggregate) >= 0
        ? this.reportColumns.filter(column => column.aggregatable)
        : this.reportColumns
      return columns.map(column => ({ label: column.name, value: column.name }))
    },
    dateFieldOptions() {
      return this.reportColumns
        .filter(column => String(column.type).toLowerCase() === 'date')
        .map(column => ({ label: column.name, value: column.name }))
    },
    // FORECAST is offered only where the server says this report has a forecast counterpart -
    // W4's availability rule, answered by the backend rather than re-derived here.
    comparePeriodOptions() {
      const forecast = !!(this.selectedReport && this.selectedReport.hasForecastCounterpart)
      return asOptions(this.options.comparePeriods)
        .filter(option => forecast || String(option.value).toUpperCase() !== 'FORECAST')
    },
    // A date field and a period together are a date window; without one there is no earlier
    // window to compare with.
    hasDateWindow() {
      return !!(this.form.dateField && this.form.datePeriod)
    },
    compareAvailable() {
      if (this.hasDateWindow) return true
      return !!(this.selectedReport && this.selectedReport.hasForecastCounterpart)
    },
    compareNote() {
      if (this.compareAvailable) return ''
      if (!this.selectedReport) return 'Choose a report, a date field and a period to compare with an earlier figure.'
      return 'This widget has no date window, so there is no earlier figure to compare it with. ' +
        'Choose a date field and a period to switch this on.'
    },
    canSave() {
      return !!(
        this.selectedReport &&
        this.form.aggregate &&
        this.form.column &&
        String(this.form.title).trim() &&
        (!this.isGroupedReport || this.form.drillTotalField)
      )
    },
    // The body of contract §6, exactly.
    widgetBody() {
      const title = String(this.form.title).trim()
      const predicate = String(this.form.predicate || '').trim()
      return {
        // The edited title is the name as well (W5c contract §5.1). A composed widget is found by
        // its id, never by its name, and the server still refuses a name another widget has.
        widgetName: title,
        widgetTitle: title,
        widgetType: this.form.widgetType || 'WIDGET',
        reportId: this.form.reportId,
        aggExpr: this.form.aggregate + '(' + this.form.column + ')',
        basePredicate: predicate || null,
        dateField: this.form.dateField || null,
        datePeriod: this.form.datePeriod || null,
        widgetIcon: this.form.icon || null,
        valueType: this.form.valueType || null,
        // A widget that cannot compare is never sent a comparison period, whatever was picked
        // before the report changed underneath it.
        widgetComparePeriod: this.compareAvailable ? this.form.comparePeriod || null : null,
        // Required for a grouped report and null for any other (W5c contract §3).
        drillTotalField: this.isGroupedReport ? this.form.drillTotalField || null : null
      }
    }
  },
  methods: {
    getOptions() {
      this.loading = true
      this.userService
        .getWidgetComposerOptions()
        .then(data => {
          this.loading = false
          const options = data && typeof data === 'object' ? data : {}
          this.options = {
            reports: Array.isArray(options.reports) ? options.reports : [],
            aggregates: Array.isArray(options.aggregates) ? options.aggregates : [],
            datePeriods: Array.isArray(options.datePeriods) ? options.datePeriods : [],
            comparePeriods: Array.isArray(options.comparePeriods) ? options.comparePeriods : [],
            valueTypes: Array.isArray(options.valueTypes) ? options.valueTypes : [],
            icons: Array.isArray(options.icons) ? options.icons : []
          }
        })
        .catch(() => {
          this.loading = false
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "widgetComposerOptions"'
          })
        })
    },
    getCatalogue() {
      this.userService
        .getDashboardWidgets()
        .then(data => {
          this.catalogue = Array.isArray(data) ? data : []
        })
        .catch(() => {
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "dashboardWidgets"'
          })
        })
    },
    // Which widget the form is about. Nothing is loaded for a legacy widget, and nothing is
    // loaded for an entry that is not in the catalogue at all: in both cases the picker stays
    // where it was, because a selection that cannot be honoured must not look honoured.
    chooseWidget(widgetId) {
      if (widgetId === this.selectedWidgetId) return

      if (widgetId === null || widgetId === undefined) {
        this.selectedWidgetId = null
        this.widgetId = null
        this.form = emptyForm()
        this.drillFilters = null
        this.rememberDrillTarget(null)
        this.errorMessage = ''
        this.savedMessage = ''
        return
      }

      const entry = (this.catalogue || []).find(item => idOf(item) === widgetId)
      if (!isEditable(entry)) return

      this.loadDefinition(widgetId)
    },
    // The settings behind a catalogue entry, in the same shape the save takes them: loaded and
    // saved untouched, the server is handed back exactly what it gave.
    loadDefinition(widgetId) {
      this.loadingDefinition = true
      this.errorMessage = ''
      this.savedMessage = ''

      this.userService
        .getDashboardWidgetDefinition(widgetId)
        .then(definition => {
          this.loadingDefinition = false
          const filled = definition && typeof definition === 'object'
            ? formFromDefinition(definition)
            : null
          if (!filled) {
            // Half a definition is worse than none: the form is left alone rather than filled
            // in with a guess that a save would then write back.
            this.errorMessage = 'That widget\'s settings could not be read, so nothing here was changed.'
            return
          }
          this.form = filled
          this.drillFilters = definition.drillFilters === undefined ? null : definition.drillFilters
          this.rememberDrillTarget(definition)
          this.widgetId = widgetId
          this.selectedWidgetId = widgetId
        })
        .catch(err => {
          this.loadingDefinition = false
          // Nothing arrived, so nothing may be overwritten: the form keeps what it had and the
          // picker stays on what is actually loaded, so the next save is still the save it was.
          this.errorMessage = this.messageFrom(
            err,
            'That widget\'s settings were not loaded, and the server did not say why.'
          )
        })
    },
    // The column, the date field and the predicate all name columns of the report that was
    // chosen: they cannot survive a change of report.
    setReport(reportId) {
      if (reportId === this.form.reportId) return
      this.form.reportId = reportId
      this.form.column = null
      this.form.dateField = null
      this.form.predicate = ''
      // A summary column belongs to the grouped report it was picked from.
      this.form.drillTotalField = null
      // The comparison is left to the watcher below: a report with no forecast counterpart and
      // no date window can no longer be compared, and the watcher is the one place that says so.
    },
    // Which report the stored widget's link opens (W5c contract §2), from a definition as the
    // server returned it; null forgets it.
    rememberDrillTarget(definition) {
      const value = key => (definition && definition[key] !== undefined ? definition[key] : null)
      this.drillReportId = value('drillReportId')
      this.drillReportName = value('drillReportName')
      // A save response that leaves reportId out was saved from this form's report.
      this.drillFromReportId = definition
        ? (value('reportId') !== null ? value('reportId') : this.form.reportId)
        : null
    },
    // count may take a column sum and avg may not, so the chosen column is kept only while the
    // new aggregate still offers it.
    setAggregate(aggregate) {
      this.form.aggregate = aggregate
      if (this.form.column && !this.columnOptions.some(o => o.value === this.form.column)) {
        this.form.column = null
      }
    },
    // The server's message, as it was written. Only when there is none at all does this screen
    // supply words of its own.
    messageFrom(err, fallback) {
      const data = err && err.response ? err.response.data : null
      if (typeof data === 'string' && data.trim()) return data.trim()
      if (data && typeof data === 'object') {
        if (typeof data.message === 'string' && data.message.trim()) return data.message.trim()
        if (typeof data.error === 'string' && data.error.trim()) return data.error.trim()
      }
      return fallback || 'The widget was not saved, and the server did not say why.'
    },
    save() {
      if (!this.canSave || this.saving) return

      this.saving = true
      this.errorMessage = ''
      this.savedMessage = ''

      const body = this.widgetBody
      const call = this.widgetId
        ? this.userService.updateDashboardWidget(this.widgetId, body)
        : this.userService.createDashboardWidget(body)

      call
        .then(saved => {
          this.saving = false
          if (saved && typeof saved === 'object') {
            const id = saved.widgetId || saved.id
            if (id) this.widgetId = id
            // The definition comes back as stored, including the drill the server has just proven.
            this.drillFilters = saved.drillFilters === undefined ? null : saved.drillFilters
            this.rememberDrillTarget(saved)
          }
          // The picker now names the widget the form is on, and the catalogue is re-read so a
          // new widget appears in it and a renamed one is relabelled.
          this.selectedWidgetId = this.widgetId
          this.getCatalogue()
          this.savedMessage = body.widgetTitle + ' was saved to the widget catalogue.'
        })
        .catch(err => {
          this.saving = false
          // Nothing is cleared: the form keeps what the user typed so they can correct the part
          // the message names and save again.
          this.errorMessage = this.messageFrom(err)
        })
    }
  },
  watch: {
    // A comparison that has become impossible is dropped rather than quietly sent.
    compareAvailable(available) {
      if (!available) this.form.comparePeriod = null
    },
    // FORECAST picked on a report that has a forecast counterpart cannot survive a move to one
    // that has not.
    comparePeriodOptions(options) {
      const chosen = this.form.comparePeriod
      if (chosen && !options.some(option => option.value === chosen)) {
        this.form.comparePeriod = null
      }
    }
  }
}
</script>

<style>
.widgetbuilder .wb-field {
  margin-bottom: 0.75rem;
}
.widgetbuilder .wb-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.widgetbuilder .wb-legacy-note,
.widgetbuilder .wb-drill-note,
.widgetbuilder .wb-compare-note {
  display: block;
  opacity: 0.7;
  margin-top: 0.25rem;
}
.widgetbuilder .wb-error {
  margin: 0.75rem 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #d32f2f;
  background: #fdecea;
}
.widgetbuilder .wb-saved {
  margin: 0.75rem 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #388e3c;
  background: #edf7ed;
}
</style>
